import React, { useState, useEffect } from 'react';
import {
    Box, TextField, Button, Typography, CircularProgress, Alert, Paper, Grid, Input,
    IconButton, Avatar, Tooltip, Stack, styled
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { createInventoryItem, updateInventoryItem } from '../../services/inventoryService';
import { toast } from 'react-toastify';
import { useLanguage } from '../../context/LanguageContext';

const ImagePreviewBox = styled(Box)(({ theme }) => ({
    width: 120,
    height: 120,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: theme.palette.grey[100],
    marginRight: theme.spacing(2),
    '& .MuiAvatar-root': {
        width: '100%',
        height: '100%',
    },
    '& .placeholder-icon': {
        fontSize: 40,
        color: theme.palette.grey[400],
    },
}));

const InventoryForm = ({ item, onSave }) => {
    const { t } = useLanguage();
    const isEditMode = Boolean(item);

    const [formData, setFormData] = useState({
        maker: '',
        model: '',
        type: '',
        quantity: '',
        notes: '',
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [existingImagePath, setExistingImagePath] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (isEditMode) {
            setFormData({
                maker: item.maker || '',
                model: item.model || '',
                type: item.type || '',
                quantity: item.quantity !== undefined ? String(item.quantity) : '',
                notes: item.notes || '',
            });
            if (item.imagePath) {
                setExistingImagePath(item.imagePath);
                setImagePreview(`${process.env.REACT_APP_API_BASE_URL || ''}${item.imagePath}`);
            }
        }
    }, [item, isEditMode]);

    const validateForm = () => {
        const errors = {};
        if (!formData.maker.trim()) errors.maker = t('validation.required', { field: t('inventory.maker') });
        if (!formData.model.trim()) errors.model = t('validation.required', { field: t('inventory.model') });
        if (!formData.type.trim()) errors.type = t('validation.required', { field: t('inventory.type') });
        if (formData.quantity === '' || isNaN(formData.quantity) || Number(formData.quantity) < 0) {
             errors.quantity = t('validation.nonNegativeNumber', { field: t('inventory.quantity') });
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error(t('inventory.imageSizeError'));
                return;
            }
            if (!file.type.startsWith('image/')) {
                 toast.error(t('inventory.imageTypeError'));
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            setExistingImagePath(null);
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setExistingImagePath(null);
        const fileInput = document.getElementById('image-upload-input');
        if (fileInput) {
            fileInput.value = null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error(t('validation.formErrorToast'));
            return;
        }

        setLoading(true);
        setError(null);

        const payload = { 
            ...formData, 
            quantity: Number(formData.quantity)
        };

        if (imageFile) {
            payload.image = imageFile;
            delete payload.removeImage; 
        } else if (!imagePreview && isEditMode && existingImagePath !== null) {
            payload.removeImage = 'true';
        }

        try {
            if (isEditMode) {
                await updateInventoryItem(item._id, payload);
                toast.success(t('inventory.updateSuccessToast'));
            } else {
                await createInventoryItem(payload);
                toast.success(t('inventory.addSuccessToast'));
            }
            onSave();
        } catch (err) {
            console.error("Error saving inventory item:", err);
            const errorMsg = err.response?.data?.message || (isEditMode ? t('inventory.updateError') : t('inventory.addError'));
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Paper sx={{ p: { xs: 2, md: 3 } }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                id="maker"
                                label={t('inventory.maker')}
                                name="maker"
                                value={formData.maker}
                                onChange={handleChange}
                                error={Boolean(formErrors.maker)}
                                helperText={formErrors.maker}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                id="model"
                                label={t('inventory.model')}
                                name="model"
                                value={formData.model}
                                onChange={handleChange}
                                error={Boolean(formErrors.model)}
                                helperText={formErrors.model}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                id="type"
                                label={t('inventory.type')}
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                error={Boolean(formErrors.type)}
                                helperText={formErrors.type}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                required
                                fullWidth
                                id="quantity"
                                label={t('inventory.quantity')}
                                name="quantity"
                                type="number"
                                InputProps={{ inputProps: { min: 0, step: "1" } }}
                                value={formData.quantity}
                                onChange={handleChange}
                                error={Boolean(formErrors.quantity)}
                                helperText={formErrors.quantity}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                id="notes"
                                label={t('inventory.notes')}
                                name="notes"
                                multiline
                                rows={4}
                                value={formData.notes}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>{t('inventory.image')}</Typography>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <ImagePreviewBox>
                                    {imagePreview ? (
                                        <Avatar 
                                            src={imagePreview} 
                                            alt={t('inventory.imagePreviewAlt')} 
                                            variant="rounded" 
                                        />
                                    ) : (
                                        <Tooltip title={t('inventory.noImagePreview')}>
                                            <BrokenImageIcon className="placeholder-icon" />
                                        </Tooltip>
                                    )}
                                </ImagePreviewBox>
                                <Stack spacing={1}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<PhotoCamera />}
                                    >
                                        {imagePreview ? t('inventory.changeImage') : t('inventory.uploadImage')}
                                        <Input 
                                            type="file" 
                                            hidden 
                                            id="image-upload-input"
                                            accept="image/*" 
                                            onChange={handleImageChange} 
                                        />
                                    </Button>
                                    {imagePreview && (
                                        <Button 
                                            variant="text" 
                                            color="error" 
                                            size="small" 
                                            startIcon={<DeleteIcon />} 
                                            onClick={handleRemoveImage}
                                        >
                                            {t('inventory.removeImage')}
                                        </Button>
                                    )}
                                    <Typography variant="caption" display="block">
                                        {t('inventory.imageUploadHint')}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                <Button 
                                    variant="outlined"
                                    onClick={onSave}
                                    disabled={loading}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    color="primary" 
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                                >
                                    {isEditMode ? t('common.saveChanges') : t('inventory.addItem')}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>
    );
};

export default InventoryForm;
